// NOTICE: This file was copied from the oppizi-shared repository, do
// not modify it here.

/**
 * @fileOverview Contains all validation rules. A validation rules is a function
 * that takes in a string as the single input and returns an error message if
 * its invalid else it returns null if the string is valid to indicate no error.
 * Syntax for a validation rule is as follows
 * ```js
 * var myValidationRule = str => {
 *   if (__custom_condition_here__){
 *     return 'The value is invalid.'
 *   }
 *   // null indicates no error
 *   return null
 * }
 */

/** -------- Generic functions           -------- **/

/**
 * Chain validation rules together, will run in sequence returning the first
 * validation rule that returns an error message.
 * @param {Function} rules Each arg can be a rule
 * @returns {Function}
 */
export const chainRules = (...rules) => str => {
  for (var i = 0; i < rules.length; i++) {
    var errorMessage = rules[i](str)
    if (errorMessage) return errorMessage
  }
  return null
}

/**
 * Pass in an object, a key value map where the key is the form field key and
 * the value is the form string value.
 * Returns an object key value map where any keys provided mean that key has
 * a validation error and the value is the validation error message.
 * @param {object} rulesObj Should be a key value map when the key is the
 * key of the field to validate and the value is a validation function.
 * @param {object} dto The key value map where the value is the string to
 * validate and the key matches the key in the rulesObj
 * @returns {object}
 *
 * Example usage
 * ```
 * var result = runValidationShared(
 *   { email: 'foo', pass: 'bar'},
 *   {
 *     email: validEmail, // This is a rule function
 *     password: validPassword,
 *   },
 * )
 * ```
 */
export const runValidation = (rulesObj, dto) => {
  var validationErrorsObj = {}
  for (var key in dto) {
    if (rulesObj[key]) validationErrorsObj[key] = rulesObj[key](dto[key])
  }
  return validationErrorsObj
}

/** -------- Generic functions end       -------- **/

/** -------- Validation rules            -------- **/

/**
 * Check string has minimum length
 * @param {string} str
 * @returns {string|null}
 */
export const minLength = minValue => (
  str,
  message = `${minValue} characters minimum`,
) => {
  if (!str || str.length < minValue) return message
  return null
}

/**
 * Check string has maximum length
 * @param {string} str
 * @returns {string|null}
 */
export const maxLength = minValue => (
  str,
  message = `${minValue} characters maximum`,
) => {
  if (str.length > minValue) return message
  return null
}

/**
 * Validate is a valid email.
 * @param {string} str
 * @returns {string|null}
 */
export const validEmail = (str, message = 'Invalid email.') => {
  const emailRegularExpression = /^([a-zA-Z0-9_.+-])+@(([a-zA-Z0-9-])+\.)+([a-zA-Z0-9]{2,4})$/
  if (!emailRegularExpression.test(str)) return message
  return null
}

/**
 * Validate password with min complexity requirements.
 * @param {string} str
 * @param {string} messages.minLength
 * @param {string} messages.minLowercase
 * @param {string} messages.minUppercase
 * @param {string} messages.minNumber
 * @returns {string|null}
 */
export const validPassword = (str, messages = {}) => {
  var {
    minLength = 'Minimum 8 characters.',
    minLowercase = 'Must have at least one lower case letter.',
    minUppercase = 'Must have at least one upper case letter.',
    minNumber = 'Must have at least one number.',
  } = messages
  if (str.length < 8) return minLength
  if (!/[a-z]{1}/.test(str)) return minLowercase
  if (!/[A-Z]{1}/.test(str)) return minUppercase
  if (!/[0-9]{1}/.test(str)) return minNumber
  return null
}

/**
 * Is required
 * @param {string} str
 * @returns {string|null}
 */
export const isRequired = str => {
  if (!str) return 'Field is required.'
}



// WEBPACK FOOTER //
// ./src/shared/validation.js